import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { apiSettings } from '../../config/apiSettings';

// Extend the InternalAxiosRequestConfig to include custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  includeAuthorization?: boolean;
}

// Request options interface
interface RequestOptions {
  contentType?: string;
  includeAuthorization?: boolean;
}

// Default content type for requests
const DEFAULT_CONTENT_TYPE = 'application/json';
const BASE_URL = process.env.REACT_APP_API_URL_BASE;

// Create an Axios instance with default headers
const axiosInstance = axios.create({
  headers: {
    'Content-Type': DEFAULT_CONTENT_TYPE,
  },
});

// Function to set the base URL based on the request URL
const setBaseURL = (config: AxiosRequestConfig) => {
  if (config.url) {
    config.baseURL = BASE_URL;
  }
};
// Refresh token function
const refreshToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  const accessToken = localStorage.getItem('accessToken');
  if (refreshToken) {
    try {
      const response = await axios.post(`${BASE_URL}${apiSettings.auth.refreshToken}`, {
        refresh_token: refreshToken,
      });
      if (response.data && response.data.access_token) {
        localStorage.setItem('accessToken', response.data.access_token);
        return response.data.access_token;
      }
    } catch (error) {
      localStorage.clear();
      window.location.href = '/#/login';
      throw error;
    }
  } else if (accessToken) {
    localStorage.clear();
    window.location.href = '/#/login';
    throw new Error('No refresh token available.');
  }
};


// Axios request interceptor
axiosInstance.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    setBaseURL(config);
    // Check if authorization should be included
    if (config.includeAuthorization !== false) {
      const token = localStorage.getItem('accessToken');
      if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error) // Pass request errors to be handled
);

// Axios response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if ((error.response?.status === 401 || error.response?.status === undefined) && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshToken();
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

// Generic request function
const makeRequest = async (
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: unknown,
  options: RequestOptions = {
    contentType: DEFAULT_CONTENT_TYPE,
    includeAuthorization: true,
  }
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method,
    url,
    data,
    headers: {
      'Content-Type': options.contentType || DEFAULT_CONTENT_TYPE,
    },
  };

  // Optionally include authorization headers
  if (options.includeAuthorization) {
    const token = localStorage.getItem('accessToken'); // or however you manage tokens
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Set the base URL based on the request URL
  setBaseURL(config);

  // Make the API request and return the response
  return axiosInstance(config);
};

// Export individual request functions
export const GET = (url: string, options?: RequestOptions): Promise<AxiosResponse> => {
  return makeRequest('GET', url, undefined, options);
};

export const POST = (url: string, data?: unknown, options?: RequestOptions): Promise<AxiosResponse> => {
  return makeRequest('POST', url, data, options);
};

export const PUT = (url: string, data?: unknown, options?: RequestOptions): Promise<AxiosResponse> => {
  return makeRequest('PUT', url, data, options);
};

export const DELETE = (url: string, options?: RequestOptions): Promise<AxiosResponse> => {
  return makeRequest('DELETE', url, undefined, options);
};
